/**
 * AddressInput - Shared address input component
 * Ported from SendPackage.jsx's proven location system
 * 
 * CONSISTENT BEHAVIOR:
 * - "Use Current Location" = Captures GPS coords ONLY, user types address
 * - "Find GPS" = Forward geocode from typed address to get coords
 * - Both use the SAME coordinate system
 * - Address is ALWAYS editable by user
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MapPin, Loader2, Navigation, CheckCircle, Locate } from "lucide-react";
import { toast } from "sonner";

const PARISHES = [
  "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary",
  "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
  "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine"
];

const JAMAICA_LANDMARKS = {
  "JUTC Halfway Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Half Way Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Halfway Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Constant Spring": { lat: 18.0258, lng: -76.7807, parish: "St. Andrew" },
  "Downtown Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "King Street Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "King Street Downtown Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "31 King Street": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "31 King Street Downtown Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "New Kingston": { lat: 18.0075, lng: -76.7833, parish: "St. Andrew" },
  "Liguanea": { lat: 18.0192, lng: -76.7633, parish: "St. Andrew" },
  "Cross Roads": { lat: 18.0042, lng: -76.7875, parish: "St. Andrew" },
  "Portmore": { lat: 17.9500, lng: -76.8833, parish: "St. Catherine" },
  "Greater Portmore": { lat: 17.9500, lng: -76.8833, parish: "St. Catherine" },
  "Spanish Town": { lat: 17.9917, lng: -76.9550, parish: "St. Catherine" },
  "Horizon Park": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "Horizon Park Spanish Town": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "3 Regal Ave": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "3 Regal Ave Horizon Park": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "Regal Ave Horizon Park Spanish Town": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "Whitewater Meadows": { lat: 17.9880, lng: -76.9450, parish: "St. Catherine" },
  "Old Harbour": { lat: 17.9417, lng: -77.1083, parish: "St. Catherine" },
  "Linstead": { lat: 18.1333, lng: -77.0333, parish: "St. Catherine" },
  "Montego Bay": { lat: 18.4762, lng: -77.8939, parish: "St. James" },
  "Ocho Rios": { lat: 18.4074, lng: -77.1051, parish: "St. Ann" },
  "Mandeville": { lat: 18.0419, lng: -77.5033, parish: "Manchester" },
  "May Pen": { lat: 17.9619, lng: -77.2478, parish: "Clarendon" },
  "Negril": { lat: 18.2678, lng: -78.3492, parish: "Westmoreland" },
  "Port Antonio": { lat: 18.1747, lng: -76.4500, parish: "Portland" },
  "Norman Manley Airport": { lat: 17.9356, lng: -76.7875, parish: "Kingston" },
  "Sangster Airport": { lat: 18.5033, lng: -77.9133, parish: "St. James" },
  "Spanish Town Hospital": { lat: 17.9920, lng: -76.9545, parish: "St. Catherine" },
  "University Hospital": { lat: 18.0156, lng: -76.7489, parish: "St. Andrew" },
  "Kingston Public Hospital": { lat: 17.9756, lng: -76.7858, parish: "Kingston" }
};

const AddressInput = ({
  label = "Location",
  value = {},
  onChange,
  placeholder = "Enter street address",
  disabled = false,
  showGpsButton = true,
  theme = "light",
  testIdPrefix = "address"
}) => {
  const [street, setStreet] = useState(value?.street || "");
  const [city, setCity] = useState(value?.city || "");
  const [parish, setParish] = useState(value?.parish || "Kingston");
  const [coordinates, setCoordinates] = useState(value?.coordinates || null);
  
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    if (value?.street !== undefined && value.street !== street) setStreet(value.street);
    if (value?.city !== undefined && value.city !== city) setCity(value.city);
    if (value?.parish !== undefined && value.parish !== parish) setParish(value.parish);
    if (value?.coordinates) setCoordinates(value.coordinates);
  }, [value]);

  const notifyChange = useCallback((updates = {}) => {
    const newValue = {
      street: updates.street !== undefined ? updates.street : street,
      city: updates.city !== undefined ? updates.city : city,
      parish: updates.parish !== undefined ? updates.parish : parish,
      coordinates: updates.coordinates !== undefined ? updates.coordinates : coordinates,
      fullAddress: `${updates.street || street}, ${updates.city || city}, ${updates.parish || parish}`.replace(/, ,/g, ",").replace(/^, /, "")
    };
    onChange?.(newValue);
  }, [street, city, parish, coordinates, onChange]);

  // Forward geocode - same logic for both "Find GPS" and suggestion selection
  const searchAddress = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }
    
    setSearching(true);
    const queryLower = query.toLowerCase();
    
    // Match local landmarks first
    const matchedLandmarks = Object.entries(JAMAICA_LANDMARKS)
      .filter(([name]) => {
        const nameLower = name.toLowerCase();
        return nameLower.includes(queryLower) || 
               queryLower.includes(nameLower.split(" ")[0]) ||
               queryLower.split(" ").some(word => word.length > 2 && nameLower.includes(word));
      })
      .map(([name, coords]) => ({
        display: name + ", " + coords.parish + ", Jamaica",
        street: name,
        city: coords.parish,
        parish: coords.parish,
        lat: coords.lat,
        lng: coords.lng,
        isLandmark: true
      }));
    
    try {
      // Nominatim forward geocode - SAME as package system
      const response = await fetch(
        "https://nominatim.openstreetmap.org/search?format=json&q=" + encodeURIComponent(query + ", Jamaica") + "&limit=5&addressdetails=1",
        { headers: { "User-Agent": "LiantiaoDeliveryApp/1.0" } }
      );
      const data = await response.json();
      
      const apiResults = data.map(item => {
        const addr = item.address || {};
        let foundParish = "Kingston";
        const state = addr.state || addr.county || addr.state_district || "";
        for (const p of PARISHES) {
          if (state.toLowerCase().includes(p.toLowerCase()) ||
              item.display_name.toLowerCase().includes(p.toLowerCase())) {
            foundParish = p;
            break;
          }
        }
        
        return {
          display: item.display_name,
          street: addr.road || addr.pedestrian || item.name || query,
          city: addr.city || addr.town || addr.village || addr.suburb || "",
          parish: foundParish,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          isLandmark: false
        };
      });
      
      setSuggestions([...matchedLandmarks, ...apiResults].slice(0, 6));
    } catch (err) {
      console.error("Address search error:", err);
      setSuggestions(matchedLandmarks);
    }
    setSearching(false);
  }, []);

  // Select suggestion - sets GPS from the SAME forward geocode results
  const selectSuggestion = (suggestion) => {
    const newCoords = { lat: suggestion.lat, lng: suggestion.lng };
    setCoordinates(newCoords);
    if (!city) setCity(suggestion.city);
    if (suggestion.parish) setParish(suggestion.parish);
    setSuggestions([]);
    notifyChange({ coordinates: newCoords, parish: suggestion.parish, city: city || suggestion.city });
    toast.success("GPS coordinates set from: " + suggestion.display.split(",")[0]);
  };

  // Use Current Location - ONLY captures GPS coords, does NOT set address
  // User must type their address, then optionally verify with "Find GPS"
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("GPS not available on this device");
      return;
    }
    
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const newCoords = { lat, lng };
        
        // Set coordinates
        setCoordinates(newCoords);
        notifyChange({ coordinates: newCoords });
        
        setGpsLoading(false);
        toast.success(
          "GPS captured! Now type your address below and click 'Find GPS' to verify.",
          { duration: 5000 }
        );
      },
      (error) => {
        setGpsLoading(false);
        if (error.code === 1) {
          toast.error("Location permission denied. Please type your address and use 'Find GPS'.");
        } else if (error.code === 3) {
          toast.error("Location timeout. Please try again or type address.");
        } else {
          toast.error("Could not get location. Please type your address.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Find GPS button - forward geocode from typed address (SAME as selecting a suggestion)
  const findGpsFromAddress = async () => {
    if (!street || street.length < 3) {
      toast.error("Please type at least 3 characters of your address");
      return;
    }
    
    setSearching(true);
    const query = street + (city ? ", " + city : "") + (parish ? ", " + parish : "");
    
    try {
      // Check landmarks first
      const queryLower = query.toLowerCase();
      for (const [name, coords] of Object.entries(JAMAICA_LANDMARKS)) {
        if (queryLower.includes(name.toLowerCase()) || name.toLowerCase().includes(queryLower.split(",")[0].trim())) {
          setCoordinates({ lat: coords.lat, lng: coords.lng });
          setParish(coords.parish);
          notifyChange({ coordinates: { lat: coords.lat, lng: coords.lng }, parish: coords.parish });
          setSearching(false);
          toast.success("GPS set from landmark: " + name);
          return;
        }
      }
      
      // Nominatim forward geocode - SAME method as suggestions
      const response = await fetch(
        "https://nominatim.openstreetmap.org/search?format=json&q=" + encodeURIComponent(query + ", Jamaica") + "&limit=1&addressdetails=1",
        { headers: { "User-Agent": "LiantiaoDeliveryApp/1.0" } }
      );
      const data = await response.json();
      
      if (data.length > 0) {
        const item = data[0];
        const addr = item.address || {};
        const newCoords = { lat: parseFloat(item.lat), lng: parseFloat(item.lon) };
        
        // Find parish
        let foundParish = parish;
        const state = addr.state || addr.county || addr.state_district || "";
        for (const p of PARISHES) {
          if (state.toLowerCase().includes(p.toLowerCase()) ||
              item.display_name.toLowerCase().includes(p.toLowerCase())) {
            foundParish = p;
            break;
          }
        }
        
        setCoordinates(newCoords);
        setParish(foundParish);
        if (!city && (addr.city || addr.town || addr.village)) {
          setCity(addr.city || addr.town || addr.village);
        }
        
        notifyChange({ coordinates: newCoords, parish: foundParish });
        toast.success("GPS coordinates found for: " + street);
      } else {
        toast.error("Could not find GPS for this address. Try adding more details or select from suggestions.");
      }
    } catch (err) {
      console.error("Find GPS error:", err);
      toast.error("Search failed. Please try again.");
    }
    setSearching(false);
  };

  const isDark = theme === "dark";
  const inputClass = isDark 
    ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
    : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400";
  const labelClass = isDark ? "text-gray-300" : "text-gray-700";
  const suggestionBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const suggestionHover = isDark ? "hover:bg-gray-700" : "hover:bg-gray-50";
  const suggestionText = isDark ? "text-gray-300" : "text-gray-700";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className={labelClass + " font-medium"}>{label}</Label>
        {coordinates && (
          <span className="text-green-500 flex items-center gap-1 text-xs">
            <CheckCircle className="w-3 h-3" /> GPS Set
          </span>
        )}
      </div>
      
      {/* GPS Capture Button - Only captures coordinates */}
      {showGpsButton && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={useCurrentLocation}
          disabled={disabled || gpsLoading}
          className={isDark 
            ? "border-cyan-700 text-cyan-400 hover:bg-cyan-900/30" 
            : "border-emerald-500 text-emerald-600 hover:bg-emerald-50"}
          data-testid={testIdPrefix + "-gps-btn"}
        >
          {gpsLoading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Getting GPS...</>
          ) : (
            <><Locate className="h-4 w-4 mr-2" /> Use My Location</>
          )}
        </Button>
      )}
      
      {/* Street Address with autocomplete */}
      <div className="relative">
        <Label className={"text-xs " + labelClass}>Street Address *</Label>
        <div className="relative">
          <Input
            placeholder={placeholder}
            value={street}
            onChange={(e) => {
              setStreet(e.target.value);
              notifyChange({ street: e.target.value });
              clearTimeout(searchTimerRef.current);
              if (e.target.value.length >= 3) {
                searchTimerRef.current = setTimeout(() => searchAddress(e.target.value), 400);
              } else {
                setSuggestions([]);
              }
            }}
            disabled={disabled}
            className={"h-11 rounded-lg " + inputClass + " pr-20"}
            data-testid={testIdPrefix + "-street"}
          />
          {searching && <Loader2 className="absolute right-14 top-3 h-4 w-4 animate-spin text-gray-400" />}
          {!searching && street.length >= 3 && (
            <button
              type="button"
              onClick={findGpsFromAddress}
              className="absolute right-2 top-1.5 px-2 py-1.5 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded font-medium"
              data-testid={testIdPrefix + "-find-gps"}
            >
              Find GPS
            </button>
          )}
        </div>
        
        {/* Suggestions dropdown */}
        {suggestions.length > 0 && (
          <div className={"absolute z-50 w-full mt-1 border rounded-lg shadow-xl max-h-48 overflow-y-auto " + suggestionBg}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => selectSuggestion(s)}
                className={"w-full px-3 py-2 text-left text-sm flex items-center gap-2 " + suggestionHover + " " + suggestionText}
                data-testid={testIdPrefix + "-suggestion-" + i}
              >
                <MapPin className={"h-4 w-4 flex-shrink-0 " + (s.isLandmark ? "text-amber-500" : "text-gray-400")} />
                <span className="truncate">{s.display}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* City and Parish */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={"text-xs " + labelClass}>City/Town</Label>
          <Input
            placeholder="City or town"
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              notifyChange({ city: e.target.value });
            }}
            disabled={disabled}
            className={"h-11 rounded-lg " + inputClass}
            data-testid={testIdPrefix + "-city"}
          />
        </div>
        <div>
          <Label className={"text-xs " + labelClass}>Parish *</Label>
          <Select 
            value={parish} 
            onValueChange={(v) => {
              setParish(v);
              notifyChange({ parish: v });
            }}
            disabled={disabled}
          >
            <SelectTrigger className={"h-11 rounded-lg " + inputClass} data-testid={testIdPrefix + "-parish"}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}>
              {PARISHES.map(p => (
                <SelectItem key={p} value={p} className={isDark ? "text-white" : "text-gray-900"}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Help text when GPS is set but no address typed */}
      {coordinates && !street && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <Navigation className="w-3 h-3" />
          GPS captured! Type your address above, then click "Find GPS" to verify coordinates match.
        </p>
      )}
    </div>
  );
};

export default AddressInput;
export { AddressInput, PARISHES, JAMAICA_LANDMARKS };
